import { i18nNsFactory } from '@atlas-frontend/i18n';
import {
  ApiAlerts,
  ApiError,
  BooleanRadio,
  CategorySelect,
  CustomColumnedCategorySelect,
  CustomPagination,
  DataSubjectFilterKey,
  DirectoryFilter,
  DirectoryHeader,
  EMPTY_PAGE,
  GetApiErrorMessage,
  InfoIconUrl,
  Main,
  MyrAlerts,
  Padding,
  PermissionCode,
  serializeValidPeriodSort,
  Stack,
  TotalEntries,
  useDataSubjectsQuery,
  useDataSubjectUpdateFromMyr,
  useDirectory,
  useHasPermission,
  useLoadViewState,
  useSaveDirectoryState,
} from '@atlas-frontend/sdk';
import { keepPreviousData } from '@tanstack/react-query';
import type { SortingState } from '@tanstack/react-table';
import { Button, FormInstance, type PaginationProps } from 'antd';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { DataSubjectTable } from '../table/DataSubjectTable';
import translations from './DataSubjectDirectory.translations.json';
import { Helmet } from 'react-helmet-async';

const { addTranslations, useTranslation, t } =
  i18nNsFactory('data-subject-table');
addTranslations(translations);

const translateApiError: GetApiErrorMessage = (code: string) =>
  t('alert.error.code.' + code);

const FILTER_KEYS: DataSubjectFilterKey[] = [
  'businessArea',
  'economicSector',
  'isFictitious',
  'isPublic',
  'legalForm',
  'search',
];

const ARRAY_FIELDS: DataSubjectFilterKey[] = [
  'businessArea',
  'economicSector',
  'legalForm',
];
const NUM_FIELDS: DataSubjectFilterKey[] = ['economicSector', 'legalForm'];
const BOOLEAN_FIELDS: DataSubjectFilterKey[] = ['isFictitious', 'isPublic'];

const directoryStateStorageKey = 'dataSubjectDirectory';

export const DataSubjectDirectory: FC = () => {
  const { state: viewState, isQueryParamCleared } = useLoadViewState(
    directoryStateStorageKey,
    {
      sort: [{ id: 'code', desc: false }],
    },
  );

  if (!isQueryParamCleared) {
    return null;
  }

  return (
    <DataSubjectDirectoryInner
      initialFilters={viewState.filters}
      initialSort={viewState.sort}
      initialPagination={viewState.pagination}
    />
  );
};

interface DataSubjectDirectoryInnerProps {
  initialFilters: Partial<Record<string, any>>;
  initialSort: SortingState;
  initialPagination: PaginationProps | undefined;
}

export const DataSubjectDirectoryInner: FC<DataSubjectDirectoryInnerProps> = (
  props,
) => {
  const { initialFilters, initialSort, initialPagination } = props;
  const { t } = useTranslation();

  const filtersRef = useRef<FormInstance>(null);

  const {
    headerProps,
    sidePanelFilterProps,
    tableProps,
    paginationProps,
    queryParameters,
    setTotal,
  } = useDirectory({
    queryFilterKeys: FILTER_KEYS,
    initialFilters,
    initialSort,
    initialPagination,
    arrayFields: ARRAY_FIELDS,
    numericFields: NUM_FIELDS,
    booleanFields: BOOLEAN_FIELDS,
    sidePanelFilterRef: filtersRef,
    serializeSort: serializeValidPeriodSort,
  });

  useSaveDirectoryState(directoryStateStorageKey, {
    paginationProps,
    queryParameters,
    tableProps,
  });

  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [errors, setErrors] = useState<ApiError[]>([]);

  useEffect(() => {
    setErrors([]);
  }, [queryParameters.search]);

  const {
    data: { content: dataSubjects, totalElements } = EMPTY_PAGE,
    error,
    isFetching: isDataSubjectsFetching,
  } = useDataSubjectsQuery(queryParameters, {
    placeholderData: keepPreviousData,
  });

  useEffect(() => {
    setTotal(totalElements);
  }, [setTotal, totalElements]);

  useEffect(() => {
    if (error) {
      setErrors(error.response?.data.errors ?? []);
    }
  }, [error]);

  const dataSource = useMemo(
    () =>
      (dataSubjects || []).map((dataSubject) => ({
        key: dataSubject.id,
        ...dataSubject,
      })),
    [dataSubjects],
  );

  const {
    refetch: myrRefetch,
    isFetching: isMyrFetching,
    isError: isMyrError,
    isSuccess: isMyrSuccess,
  } = useDataSubjectUpdateFromMyr(undefined, {
    enabled: false,
  });

  const handleUpdateFromMyr = useCallback(() => myrRefetch(), [myrRefetch]);
  const hasPermissionToUpdateFromMyr = useHasPermission(
    PermissionCode.SUBJECT_LIST_LOAD,
  );

  const filterFields = useMemo(
    () => [
      {
        name: 'businessArea',
        label: t('filter.businessArea'),
        initialValue: queryParameters.businessArea,
        input: () => (
          <CustomColumnedCategorySelect
            id="businessArea"
            placement="bottomRight"
          />
        ),
      },
      {
        name: 'economicSector',
        label: t(`filter.economicSector`),
        initialValue: queryParameters.economicSector,
        input: () => (
          <CategorySelect
            id="INST_SECTOR-filter"
            code="INST_SECTOR"
            showSearch={false}
          />
        ),
      },
      {
        name: 'legalForm',
        label: t(`filter.legalForm`),
        initialValue: queryParameters.legalForm,
        input: () => (
          <CategorySelect
            id="LEGAL_FORM-filter"
            code="LEGAL_FORM"
            showSearch={false}
          />
        ),
      },
      {
        name: 'isFictitious',
        label: t(`filter.isFictitious`),
        initialValue: queryParameters.isFictitious,
        input: () => <BooleanRadio id="isFictitious" />,
      },
      {
        name: 'isPublic',
        label: t(`filter.isPublic`),
        initialValue: queryParameters.isPublic,
        input: () => <BooleanRadio id="isPublic" />,
      },
    ],
    [
      queryParameters.businessArea,
      queryParameters.economicSector,
      queryParameters.isFictitious,
      queryParameters.isPublic,
      queryParameters.legalForm,
      t,
    ],
  );

  return (
    <>
      <Helmet>
        <title>{t('title')}</title>
      </Helmet>
      <Main>
        <Main.Col border={false}>
          <Main.Content padding={false} id="data-subject-directory-content">
            <MyrAlerts
              isFetching={isMyrFetching}
              isSuccess={isMyrSuccess}
              isError={isMyrError}
            />
            <ApiAlerts
              errors={errors}
              onChange={setErrors}
              translateError={translateApiError}
            />
            <ContentWrapper>
              <DirectoryHeader
                title={t('title')}
                infoIconUrl={InfoIconUrl.DATA_SUBJECT_DIRECTORY}
                {...headerProps}
                isFiltersOpen={isFiltersOpen}
                onFilterToggle={() => setIsFiltersOpen((current) => !current)}
                extraActions={
                  hasPermissionToUpdateFromMyr && (
                    <Button
                      id="update-from-myr"
                      type="primary"
                      onClick={handleUpdateFromMyr}
                      loading={isMyrFetching}
                    >
                      {t('button.update-from-myr')}
                    </Button>
                  )
                }
              />
              <Padding horizontal bottom>
                <DataSubjectTable
                  {...tableProps}
                  data={dataSource}
                  isLoading={isDataSubjectsFetching}
                />
              </Padding>
            </ContentWrapper>
          </Main.Content>
          <Main.Footer>
            <TotalEntries count={totalElements} />
            <FooterPagination {...paginationProps} />
          </Main.Footer>
        </Main.Col>
        {isFiltersOpen && (
          <DirectoryFilter
            ref={filtersRef}
            {...sidePanelFilterProps}
            onClose={() => setIsFiltersOpen(false)}
            fields={filterFields}
          />
        )}
      </Main>
    </>
  );
};

const FooterPagination = styled(CustomPagination)`
  margin-left: auto;
`;

const ContentWrapper = styled(Stack)`
  width: 100%;
  max-width: 120em;
  height: 100%;
  align-self: center;
`;
